import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { auditTime, fromEvent, interval, merge } from 'rxjs';
import { AuthService } from '../auth/auth.service';

const IDLE_WARNING_MS = 28 * 60 * 1000;
const LOCAL_IDLE_LIMIT_MS = 30 * 60 * 1000;
const ABSOLUTE_WARNING_MS = 15 * 60 * 1000;
const ACTIVITY_REFRESH_AFTER_MS = 5 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class AdminSessionTimeoutService {
  private readonly auth = inject(AuthService);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  private started = false;
  private lastInteractionAt = Date.now();
  private lastActivityRefreshAt = 0;
  private refreshPending = false;

  readonly idleWarningVisible = signal(false);
  readonly absoluteWarningVisible = signal(false);
  readonly absoluteEndsAt = signal<Date | null>(null);

  start(): void {
    if (this.started) return;
    this.started = true;

    merge(
      fromEvent(this.document, 'pointerdown'),
      fromEvent(this.document, 'keydown'),
      fromEvent(this.document, 'touchstart'),
    )
      .pipe(auditTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onInteraction());

    fromEvent(this.document, 'visibilitychange')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.document.visibilityState === 'visible' && this.auth.isAuthenticated()) {
          // Visibility is not proof of human activity. Re-evaluate the server
          // deadlines, but never extend them merely because a laptop woke up.
          this.evaluate();
        }
      });

    interval(15_000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.evaluate());
    this.evaluate();
  }

  staySignedIn(): void {
    this.lastInteractionAt = Date.now();
    this.idleWarningVisible.set(false);
    this.refreshFromServer();
  }

  signOutNow(): void {
    this.auth.logout().subscribe({ error: () => undefined });
  }

  /** Exposed for deterministic timer tests and resume handling. */
  evaluate(now = Date.now()): void {
    if (!this.auth.isAuthenticated()) {
      this.resetWarnings();
      return;
    }

    const timing = this.auth.sessionTiming();
    const idleExpiresAt = this.parseTime(timing?.idleExpiresAt);
    const absoluteExpiresAt = this.parseTime(timing?.absoluteExpiresAt);

    if (
      (idleExpiresAt !== null && now >= idleExpiresAt) ||
      (absoluteExpiresAt !== null && now >= absoluteExpiresAt)
    ) {
      this.auth.forceLogout('expired');
      this.resetWarnings();
      return;
    }

    if (absoluteExpiresAt !== null) {
      this.absoluteEndsAt.set(new Date(absoluteExpiresAt));
      this.absoluteWarningVisible.set(absoluteExpiresAt - now <= ABSOLUTE_WARNING_MS);
    } else {
      this.absoluteEndsAt.set(null);
      this.absoluteWarningVisible.set(false);
    }

    const serverLastUsed = this.parseTime(timing?.lastUsedAt);
    const effectiveLastUse = Math.max(this.lastInteractionAt, serverLastUsed ?? 0);
    const localIdle = now - effectiveLastUse;
    this.idleWarningVisible.set(localIdle >= IDLE_WARNING_MS);

    // The ceiling is anchored to the last server timestamp (kept warm while the
    // user is active) as well as browser interaction, never to a timer started
    // blindly at page load.
    if (localIdle >= LOCAL_IDLE_LIMIT_MS) {
      this.auth.forceLogout('expired');
      return;
    }

    if (
      localIdle < 60_000 &&
      serverLastUsed !== null &&
      now - serverLastUsed >= ACTIVITY_REFRESH_AFTER_MS &&
      now - this.lastActivityRefreshAt >= ACTIVITY_REFRESH_AFTER_MS
    ) {
      this.refreshFromServer();
    }
  }

  private onInteraction(): void {
    this.lastInteractionAt = Date.now();
    if (this.idleWarningVisible()) this.idleWarningVisible.set(false);
    this.evaluate();
  }

  private refreshFromServer(): void {
    if (this.refreshPending) return;
    this.refreshPending = true;
    this.lastActivityRefreshAt = Date.now();
    this.auth.refreshToken().subscribe({
      next: () => {
        this.refreshPending = false;
        this.lastInteractionAt = Date.now();
        this.evaluate();
      },
      error: () => {
        // Transient failures deliberately retain the session. Definitive errors
        // have already been routed through AuthService's central invalidator.
        this.refreshPending = false;
      },
    });
  }

  private resetWarnings(): void {
    this.idleWarningVisible.set(false);
    this.absoluteWarningVisible.set(false);
    this.absoluteEndsAt.set(null);
  }

  private parseTime(value?: string): number | null {
    if (!value) return null;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
}
